import React from 'react';
import { Box } from '@mui/material';
import { useConfirm, useLocales } from '../../../../hooks';
import { makeStyles } from 'tss-react/mui';
import { FormProvider, useForm } from 'react-hook-form';
import Button from '../../Button';
import {
  ContentCollectionType,
  ContentGroupRegionsBody,
  ContentGroupsRegionsBody,
  ContentGroupsType,
  MediaResponse,
  RedirectionType as EnrichRedirectionType,
  ReverseOrder
} from '../../../../API';
import { useData } from '../../../../data-layer';
import { usePermissionsGuard } from '../../../../hooks/General/usePermissionsGuard';
import { HomepageOptions } from '../../../../state/theme';
import { useRecoilValue } from 'recoil';
import { enrichTestIds } from '../../TestsIds';
import { ReverseChronological } from './ReverseChronological';
import { ContentEntitlementFiltering as ContentEntitlementFilteringField } from './ContentEntitlementFiltering';
import { DeepLink } from './DeepLink';
import { SearchSynonyms } from './SearchSynonyms';
import { isEqual } from 'lodash-es';

interface EnrichFormProps {
  contentId?: string;
}

// Reverse Chronological orders seasons and episodes, so only episodic content can use it. The API rejects
// the field with a 422 for every other type, so this has to be an allow list: a type missing from it is a
// video as far as the API is concerned, even if the CMS does not know about it yet.
const contentTypesWithReverseChronological: string[] = [
  ContentCollectionType.SERIES,
  ContentCollectionType.NOVELA,
  ContentCollectionType.SHORT_NOVELA
];

// Search synonyms are authored per VOD asset. This mirrors the allow list the API guards the patch
// with, so an editor is never shown a field the save would reject
const contentTypesWithSynonyms: string[] = [
  ContentCollectionType.MOVIE,
  ContentCollectionType.SERIES,
  ContentCollectionType.NOVELA,
  ContentCollectionType.SHORT_NOVELA,
  ContentCollectionType.CLIP
];

export interface ContentEntitlementFiltering {
  contentGroup: ContentGroupsType[];
  countryCodes: string[];
}

export interface EnrichForm {
  reverseChronologicalOrder: string;
  contentEntitlementFiltering: ContentEntitlementFiltering[];
  redirection: { type: EnrichRedirectionType; value: string };
  synonyms: string[];
}

function getEnrichDefaultValues(content?: MediaResponse): EnrichForm {
  const defaultValues = {} as EnrichForm;
  const contentType = content?.contentType ?? '';

  if (contentTypesWithReverseChronological.includes(contentType)) {
    defaultValues.reverseChronologicalOrder = content?.reverseOrder || ReverseOrder.NONE;
  }

  if (contentTypesWithSynonyms.includes(contentType)) {
    defaultValues.synonyms = content?.synonyms ?? [];
  }

  defaultValues.contentEntitlementFiltering =
    content?.contentGroupsRegions?.map(({ contentGroup, countryCodes }) => ({
      contentGroup,
      countryCodes
    })) ?? [];

  const redirection = content?.extendedMetadata?.redirection;
  defaultValues.redirection = {
    type: redirection?.type ?? ('' as EnrichRedirectionType),
    value: redirection?.value ?? ''
  };

  return defaultValues;
}

export const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(4)
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4)
  },
  saveAndPublishBtn: {
    marginTop: theme.spacing(4)
  }
}));

export function EnrichForm({ contentId }: EnrichFormProps): JSX.Element {
  const { classes } = useStyles();
  const {
    media: {
      state: { withRecordById, withSearchParams },
      hook: { closeForm, patchContent, clearPersistedSearchParams }
    }
  } = useData();
  const { t } = useLocales();
  const { confirm } = useConfirm();
  const { canPublish: canPublishReverseChron } = usePermissionsGuard({
    homepageOption: HomepageOptions.METADATA
  });
  const content = useRecoilValue(withRecordById(contentId));
  const lastSearch = useRecoilValue(withSearchParams);

  const hasReverseChronological = contentTypesWithReverseChronological.includes(content?.contentType ?? '');
  const hasSynonyms = contentTypesWithSynonyms.includes(content?.contentType ?? '');

  const storedSynonyms = content?.synonyms ?? [];

  const methods = useForm<EnrichForm>({
    defaultValues: getEnrichDefaultValues(content)
  });
  const { control } = methods;

  const [isSaving, setIsSaving] = React.useState(false);

  const handleSubmit = async (data: EnrichForm) => {
    if (contentId) {
      const isConfirmed = await confirm({
        confirmText: t('general.update'),
        confirmColor: 'warning',
        body: t('confirm.enrich_update'),
        'data-testid': enrichTestIds.updateConfirm
      });

      if (!isConfirmed) return;

      const reverseOrder = data.reverseChronologicalOrder as ReverseOrder;
      let contentGroups: ContentGroupRegionsBody | undefined = undefined;

      if (data.contentEntitlementFiltering.length > 0) {
        const regions: ContentGroupsRegionsBody[] = [];
        data.contentEntitlementFiltering.forEach(({ contentGroup, countryCodes }) => {
          regions.push({
            contentGroup,
            countryCodes
          });
        });
        contentGroups = { regions };
      }

      // Content search does not index synonyms yet, so reopening an asset in a new session shows an
      // empty box even when the asset has them stored. Sending the field only once the editor has
      // actually changed it keeps an unrelated save from wiping those stored values.
      const synonymsChanged = hasSynonyms && !isEqual(data.synonyms ?? [], storedSynonyms);

      setIsSaving(true);
      const content = await patchContent(contentId, {
        // Content types without the section must not send the field at all, the API rejects it
        ...(hasReverseChronological && { reverseOrder }),
        ...(synonymsChanged && { synonyms: data.synonyms }),
        contentGroups,
        lastSearch,
        ...(data.redirection?.value && {
          extendedMetadata: {
            redirection: {
              type: data.redirection.type,
              value: data.redirection.value
            }
          }
        })
      });

      if (content) {
        clearPersistedSearchParams();
        closeForm();
        return;
      }
      setIsSaving(false);
    }
  };

  const handleSaveAndPublish = async () => {
    methods.handleSubmit(handleSubmit)();
  };

  return (
    <Box className={classes.root} data-testid={enrichTestIds.formBody}>
      <FormProvider {...methods} handleSubmit={methods.handleSubmit}>
        <Box className={classes.formContainer}>
          {hasReverseChronological && <ReverseChronological control={control} isSaving={isSaving} />}
          {hasSynonyms && <SearchSynonyms control={control} isSaving={isSaving} />}
          <ContentEntitlementFilteringField control={control} isSaving={isSaving} />
          <DeepLink control={control} isSaving={isSaving} />
        </Box>
        <Box className={classes.saveAndPublishBtn}>
          <Button onClick={handleSaveAndPublish} disabled={!canPublishReverseChron || isSaving}>
            {t('general.update')}
          </Button>
        </Box>
      </FormProvider>
    </Box>
  );
}
